/**
 * Navigation Stack Converter Module
 *
 * Provides unified functions to convert between the Angular navigation stack
 * and the PageWithParams[] format used by the URL parser.
 *
 * This centralizes all navigation stack conversion logic to ensure consistency.
 */

import { Page } from 'providers/nav-controller';
import { PageWithParams, pagesToUrl } from './url-parser';
import { getPageSegment } from '../app/app-page-helpers';

/**
 * Logger interface for tracking abnormal errors.
 * Compatible with LoggingService.trackAbnormalError.
 */
export interface NavStackLogger {
  trackAbnormalError(message: string): void;
}

/**
 * Convert the current navigation stack to PageWithParams array.
 *
 * @param pages Array of Page objects from nav controller
 * @param logger Logger for tracking abnormal errors
 * @returns Array of PageWithParams representing the navigation stack
 */
export function navStackToPages(pages: Page[], logger: NavStackLogger): PageWithParams[] {
  if (!pages || pages.length === 0) {
    return [];
  }

  const result: PageWithParams[] = [];

  for (const page of pages) {
    const pageSegment = getPageSegment(page.component.componentType);
    if (pageSegment) {
      // the data property holds the navigation parameters; they go through the same
      // filter as the root-page address and the history stack, so all three agree
      const stringParams = filterUrlParameters(page.data, logger);

      result.push({
        segment: pageSegment,
        params: stringParams
      });
    }
  }

  return result;
}

/**
 * Get the breadcrumb trail from a navigation stack.
 * The breadcrumb includes all pages EXCEPT the last one (current page).
 *
 * @param pages Array of Page objects from nav controller
 * @param logger Logger for tracking abnormal errors
 * @returns Array of PageWithParams representing the breadcrumb trail
 */
export function navStackToBreadcrumb(pages: Page[], logger: NavStackLogger): PageWithParams[] {
  if (!pages || pages.length <= 1) {
    return [];
  }

  // Return all pages except the last one
  return navStackToPages(pages.slice(0, -1), logger);
}

/**
 * Extract only URL-relevant parameters from page data.
 * Filters out navigation-only data that shouldn't be in URLs.
 *
 * Every serializable parameter is kept. The runtime-only ones are dropped:
 * `_callId` is a live call handle, and `local` is held in sessionStorage by
 * `storeLocalParam()` — neither means anything in an address.
 *
 * #5124 / df-a776d36e5110: this function used to take a page definition as well, and
 * with one it kept only the parameters the definition's route segment declared as
 * `:<name>`. No page segment declares such a placeholder any more, so that arm always
 * returned nothing and every parameter was dropped — which is how the navigation
 * back-trail lost the meal, food or day each earlier page was showing. The arm is gone
 * rather than merely unused, so a future caller cannot ask for it again. `pagesToUrl`
 * writes the parameters in the `:name~value` form the app's own parser reads back.
 *
 * @param params All parameters from the page
 * @param logger Logger for tracking abnormal errors
 * @returns Only the parameters that should be included in URLs
 */
export function filterUrlParameters(
  params: any,
  logger: NavStackLogger
): Record<string, string> {
  if (!params || Object.keys(params).length === 0) {
    return {};
  }

  // convert all params to strings
  const stringParams: Record<string, string> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined) {
      if (typeof value === 'function') {
        logger.trackAbnormalError(`nav-stack-converter: Skipping non-serializable parameter: ${key} (${typeof value})`);
        stringParams[key] = '';
      } else if (key === '_callId' || key === 'local') {
        // _callId is a runtime-only parameter - skip from URL
        // local is stored in sessionStorage via storeLocalParam() - skip from URL
      } else if (typeof value === 'object') {
        // objects should be decomposed differently by the caller!
        logger.trackAbnormalError(`nav-stack-converter: Skipping non-serializable parameter: ${key} (${typeof value})`);
        stringParams[key] = '';
      } else {
        // localId and other string params are handled here
        stringParams[key] = String(value);
      }
    } else {
      stringParams[key] = '';
    }
  }
  return stringParams;
}

/**
 * Build the address for a root page that has no live navigation stack behind it.
 *
 * Used when the nav view is not mounted (during boot, behind the splash) and when
 * a root page is set by name. There is no `Page` object to read, so the segment and
 * the parameters are given directly, and the parameters go through the same filter
 * `navStackToPages` uses — so a runtime-only value never reaches the address.
 *
 * #5124: the callers used to build this address by substituting `:<name>` placeholders
 * into the page's route path. No page segment declares such a placeholder any more, so
 * that always dropped every parameter — which is how a shared recipe link opened an
 * empty New Food page. `pagesToUrl` writes them in the `:name~value` form the app's own
 * parser reads back.
 */
export function pageRootUrl(pageSegment: string, params: any, logger: NavStackLogger): string {
  return pagesToUrl([{ segment: pageSegment, params: filterUrlParameters(params, logger) }]);
}

/**
 * Build one address per level of a page stack that is being written into browser history:
 * entry i names pages 0..i, each with its own parameters.
 *
 * Used by `NavController.setPagesAsync`, which rebuilds the whole history stack from the
 * pages it was given. The parameters go through the same filter `navStackToPages` uses, so a
 * runtime-only value never reaches the address.
 *
 * df-1ec3f3d10d40: those addresses used to be built with `UrlService.serializeComponent`,
 * which emits no parameters at all, so every entry was a bare page segment, and going Back
 * or Forward onto one reopened its page without the item it was showing. (It used to look
 * like a selection — it kept the parameters a route segment declared as `:<name>`, and no
 * segment declares one — but that scan was dead and df-3a1c41a56b8b deleted it without
 * changing any value it produces.)
 */
export function stackHistoryUrls(pages: { segment: string; params: any }[], logger: NavStackLogger): string[] {
  const converted: PageWithParams[] = pages.map(p => ({
    segment: p.segment,
    params: filterUrlParameters(p.params, logger),
  }));
  return converted.map((_, i) => pagesToUrl(converted.slice(0, i + 1)));
}

/**
 * Check if a navigation stack represents the same pages as a PageWithParams array.
 * This is useful for detecting duplicate navigation entries.
 *
 * @param navStack Array of Page objects
 * @param pages Array of PageWithParams
 * @param logger Logger for tracking abnormal errors
 * @returns True if they represent the same navigation state
 */
export function isSameNavigation(navStack: Page[], pages: PageWithParams[], logger: NavStackLogger): boolean {
  const navPages = navStackToPages(navStack, logger);

  if (navPages.length !== pages.length) {
    return false;
  }

  for (let i = 0; i < navPages.length; i++) {
    if (navPages[i].segment !== pages[i].segment) {
      return false;
    }

    // Check if params are the same (ignoring empty vs missing)
    const navParams = navPages[i].params;
    const pageParams = pages[i].params;

    const navKeys = Object.keys(navParams).filter(k => navParams[k]);
    const pageKeys = Object.keys(pageParams).filter(k => pageParams[k]);

    if (navKeys.length !== pageKeys.length) {
      return false;
    }

    for (const key of navKeys) {
      if (navParams[key] !== pageParams[key]) {
        return false;
      }
    }
  }

  return true;
}